import { useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@clarix/auth-store';
import { AlreadySignedIn } from '@/platform/auth/AlreadySignedIn';

/**
 * The public auth routes this guard wraps — never a valid `?next` target.
 *
 * NOT '/workspace': that is the Workspace app's home and a perfectly good place
 * to send someone after signing in. Listing it here would silently drop the
 * `?next` that brought them.
 */
const AUTH_PATHS = ['/login', '/signup', '/verify-otp', '/forgot-password'];

/**
 * Sanitise the `?next=` hint RequireAuth writes. Only a same-origin, path-relative
 * target is accepted (no `//evil.com`, no absolute URL), and never another auth
 * route — that would bounce straight back into this guard.
 */
function safeNext(raw: string | null): string | null {
  if (!raw) return null;
  let path: string;
  try {
    path = decodeURIComponent(raw);
  } catch {
    return null;
  }
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  if (AUTH_PATHS.some((p) => path === p || path.startsWith(`${p}?`))) return null;
  return path;
}

/**
 * Inverse of RequireAuth: the public auth routes (/login, /signup, …).
 *
 * A user who is ALREADY signed in and lands here is sent straight on to their
 * workspace — no interstitial, no button to press. The old "You're already
 * signed in → Continue to your workspace" card made every arrival cost a click,
 * and during a backend outage it was actively misleading: the only thing behind
 * the button was a workspace that couldn't load. Backend health is now decided
 * where it belongs — WorkspaceShell + AppStatusGate show the self-recovering
 * maintenance page — so this guard only ever answers the auth question.
 *
 * Two escape hatches keep account switching reachable:
 *   • `?switch=1` — explicit "sign in as someone else"; renders {@link AlreadySignedIn}.
 *   • `?force-logout=1` — set by a cross-origin sign-out; main.tsx wipes this
 *     origin's token BEFORE hydration, so the token is already null here.
 */
export function RequireUnauthed() {
  const token = useAuthStore((s) => s.token);
  const workspaceSlug = useAuthStore((s) => s.workspaceSlug);
  const location = useLocation();

  // Was the user already signed in when this route mounted? A token that appears
  // LATER belongs to a sign-in completing on this very page — LoginPage owns that
  // navigation (2FA gates, onboarding, the cross-subdomain `#token=` hop), so we
  // must never pre-empt it with a redirect of our own.
  // Lazy initialiser → read once, on mount, and never again.
  const [arrivedSignedIn] = useState(() => !!useAuthStore.getState().token);

  if (!token || !arrivedSignedIn) return <Outlet />;

  const params = new URLSearchParams(location.search);
  if (params.get('switch') === '1') return <AlreadySignedIn />;

  // Where an already-signed-in arrival goes: their workspace root if the session
  // is workspace-bound, otherwise the create-workspace form. Every session here
  // is a tenant session — the platform console signs in on its own origin.
  const next = safeNext(params.get('next'));
  const home = workspaceSlug ? '/' : '/onboarding';
  return <Navigate to={next ?? home} replace />;
}
